import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { keyboardPinStyle, useKeepFocusedFieldVisible, useKeyboardBox } from "@/hooks/useKeyboardBox";
import { dialogCenteredBase, dialogCloseBase, dialogSheetBase } from "@/components/ui/overlay-classes";


const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-overlay bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    // Lets a caller raise the backdrop's z-index in lockstep with the content
    // (e.g. a fullscreen map that must sit above the sticky navbar).
    overlayClassName?: string;
    /**
     * Off for full-screen dialogs that already follow the visible area with
     * their own classes (the concierge overlay): the pin would shift them
     * half a screen to the left.
     */
    keyboardPin?: boolean;
    /**
     * Under `sm` the dialog is a bottom sheet with a sticky title and sticky
     * actions (see overlay-classes.ts and index.css). Off for dialogs that
     * lay themselves out (full screen, tall flex boxes): they keep the
     * centred box on every width.
     */
    phoneSheet?: boolean;
  }
>(({ className, overlayClassName, children, style, keyboardPin = true, phoneSheet = true, onFocus, ...props }, ref) => {
  const box = useKeyboardBox();
  const kb = keyboardPin ? box : null;
  const keyboardStyle = keyboardPinStyle(kb, phoneSheet ? "sheet" : "centered");
  const inner = React.useRef<HTMLDivElement | null>(null);
  const reveal = useKeepFocusedFieldVisible(inner, kb);
  const setRef = React.useCallback((node: HTMLDivElement | null) => {
    inner.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [ref]);
  return (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={setRef}
      style={{ ...style, ...keyboardStyle }}
      data-keyboard={kb ? "open" : undefined}
      data-phone-sheet={phoneSheet ? "true" : undefined}
      onFocus={(e) => { onFocus?.(e); reveal?.(); }}
      className={cn(phoneSheet ? dialogSheetBase : dialogCenteredBase, className)}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className={cn(dialogCloseBase, "data-[state=open]:bg-terracotta-light data-[state=open]:text-muted-foreground")}>
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // data-dialog-header: sticky at the top of a phone sheet (index.css).
  <div data-dialog-header="" className={cn("flex flex-col space-y-1.5 px-4 text-center sm:px-0 sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // data-dialog-footer: sticky above the keyboard on a phone sheet (index.css).
  <div data-dialog-footer="" className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
